import React, { useState } from 'react'
import { Button } from 'reactstrap';
import { authEndpoints, endpoints } from '../../utils/Constants';
import { Input } from 'reactstrap'
import { useEffect } from 'react';
import LoadingAnimation from '../Form/LoadingAnimation';
import useFetchSimple from '../../Services/useFetchSimple';
import useFetch from '../../Services/useFetch';
import Selector from '../Form/SelectorNoForm';
import { IAirportLookup } from '../../Models/IAirportLookup';
import { useFetchCached } from '../../Services/fetchApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { exportToExcel } from '../../utils/exportExcel';

interface StatusMessage { message: string; type: 'success' | 'warning' | 'danger'; }

interface rowProps {
    options: string[];
    newItem?: boolean;
    lookup: IAirportLookup;
    updateLookup: (item: IAirportLookup) => Promise<boolean>;
    saveNewLookup: (item: IAirportLookup) => Promise<boolean>;
    deleteLookup: (item: IAirportLookup) => Promise<void>;
    cancelNew?: () => void;
    onStatus: (s: StatusMessage) => void;
}

interface LookupSnapshot {
    site: string;
    state: string;
    altState: string;
    iataCode: string;
    timeZoneId: string;
    manualUtcOffset: string;
}

const parseUtcOffset = (value?: string | null): string | null => {
    if (!value?.trim()) return '';

    const trimmed = value.trim().toUpperCase();
    const normalized = trimmed.startsWith('UTC') ? trimmed.substring(3) : trimmed;
    const match = normalized.match(/^([+-])(\d{1,2})(?::(\d{2}))?$/);
    if (!match) {
        return null;
    }

    const hours = Number.parseInt(match[2], 10);
    const minutes = Number.parseInt(match[3] ?? '00', 10);
    if (Number.isNaN(hours) || Number.isNaN(minutes) || minutes < 0 || minutes > 59) {
        return null;
    }
    // Real-world UTC offsets: -12:00 to +14:00 (-720 to +840 minutes)
    const signMultiplier = match[1] === '-' ? -1 : 1;
    const totalMinutes = signMultiplier * (hours * 60 + minutes);
    if (totalMinutes < -720 || totalMinutes > 840) {
        return null;
    }

    return `${match[1]}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};


const LookupRow = ({ options, newItem, lookup, updateLookup, saveNewLookup, deleteLookup, cancelNew, onStatus }: rowProps) => {
    const { site, state, altState, iataCode, timeZoneId, manualUtcOffset } = lookup;
    const [newSite, setNewSite] = useState(site);
    const [deleteItem, setDeleteItem] = useState(false);
    const [selection, setSelection] = useState(state);
    const [altSelection, setAltSelection] = useState(altState || '');
    const [newIataCode, setNewIataCode] = useState(iataCode ?? "");
    const [newTimeZoneId, setNewTimeZoneId] = useState(timeZoneId ?? "");
    const [newManualUtcOffset, setNewManualUtcOffset] = useState(manualUtcOffset ?? "");
    const [saving, setSaving] = useState(false);
    const [siteError, setSiteError] = useState(false);
    const [stateError, setStateError] = useState(false);
    const [manualOffsetError, setManualOffsetError] = useState(false);
    const [savedSnapshot, setSavedSnapshot] = useState<LookupSnapshot>({
        site: site ?? '',
        state: state ?? '',
        altState: altState ?? '',
        iataCode: iataCode ?? '',
        timeZoneId: timeZoneId ?? '',
        manualUtcOffset: manualUtcOffset ?? ''
    });

    /**
     * Determines whether the row is currently different than its lookup
     */
    const different = () => {
        if (!savedSnapshot.site && !savedSnapshot.state && !savedSnapshot.altState) return false;
        if (savedSnapshot.site !== newSite) return true;
        if (savedSnapshot.state !== selection) return true;
        if (savedSnapshot.altState !== altSelection) return true;
        if (savedSnapshot.iataCode !== newIataCode) return true;
        if (savedSnapshot.timeZoneId !== newTimeZoneId) return true;
        if (savedSnapshot.manualUtcOffset !== newManualUtcOffset) return true;
        return false;
    };
    useEffect(() => {
        setSavedSnapshot({
            site: site ?? '',
            state: state ?? '',
            altState: altState ?? '',
            iataCode: iataCode ?? '',
            timeZoneId: timeZoneId ?? '',
            manualUtcOffset: manualUtcOffset ?? ''
        });
    }, [lookup.id, site, state, altState, iataCode, timeZoneId, manualUtcOffset]);
    useEffect(() => {
        if (site) {
            setNewSite(site);
        }
    }, [site]);
    useEffect(() => {
        if (options && state) {
            setSelection(state);
        }
    }, [state, options]);
    useEffect(() => {
        if (options && altState) {
            setAltSelection(altState);
        }
    }, [altState, options]);
    useEffect(() => {
        if (iataCode) {
            setNewIataCode(iataCode);
        }
    }, [iataCode]);
    useEffect(() => {
        setNewTimeZoneId(timeZoneId ?? "");
    }, [timeZoneId]);
    useEffect(() => {
        setNewManualUtcOffset(manualUtcOffset ?? "");
    }, [manualUtcOffset]);

    if (!lookup) return null;
    const missingTimezone = !newItem && !newTimeZoneId?.trim() && !newManualUtcOffset?.trim();
    return <tr style={missingTimezone ? { background: '#fff8e1' } : undefined}>
        <td>
            <Input type="text" value={newSite} className={siteError ? 'is-invalid' : ''}
                title="Name of the airport or landing site"
                onChange={(e: { target: { value: string } }) => { setNewSite(e.target.value); if (e.target.value.trim()) setSiteError(false); }} />
            {siteError && <div className="invalid-feedback d-block">Site name is required.</div>}
        </td>
        <td title="State or territory - used to determine the timezone when no exact match is found">
            <Selector value={selection} onChange={({ value }) => { setSelection(value); if (value?.trim()) setStateError(false); }} options={options} />
            {stateError && <div className="invalid-feedback d-block">State is required.</div>}
        </td>
        <td title="Alternative state for border sites that operate on a different timezone (e.g. Broken Hill is NSW but uses SA time)"><Selector value={altSelection} onChange={({ value }) => {
            if (value === 'Clear') setAltSelection('');
            else setAltSelection(value);
        }} options={['Clear'].concat(options)} /></td>
        <td style={{ width: 60 }}><Input type="text" value={newIataCode} title="3-letter airport code (e.g. BNE). Used to match routes that reference an airport by code instead of full name" onChange={(e: { target: { value: string } }) => { setNewIataCode(e.target.value) }} /></td>
        <td style={{ minWidth: 180 }}>
            <Input type="text" value={newTimeZoneId} placeholder="e.g. Australia/Sydney"
                title="Exact timezone for this site (e.g. Australia/Adelaide). Overrides the state default - required for correct local times on itineraries"
                style={missingTimezone ? { borderColor: '#f0ad4e' } : undefined}
                onChange={(e: { target: { value: string } }) => { setNewTimeZoneId(e.target.value) }} />
            {missingTimezone && <small className="text-warning">Add timezone or UTC offset</small>}
        </td>
        <td style={{ minWidth: 140 }}>
            <Input type="text" value={newManualUtcOffset} placeholder="e.g. +10:00"
                title="Optional fixed UTC offset override (format: +HH:mm or -HH:mm). Requires Timezone (IANA) to be set for this airport."
                className={manualOffsetError ? 'is-invalid' : ''}
                onChange={(e: { target: { value: string } }) => { setNewManualUtcOffset(e.target.value); if (manualOffsetError) setManualOffsetError(false); }} />
            {manualOffsetError && <div className="invalid-feedback d-block">{newManualUtcOffset?.trim() && newTimeZoneId?.trim() === '' ? 'Timezone (IANA) is required when using manual offset.' : 'Use signed offset like +10:00 or -05:30.'}</div>}
        </td>
        <td style={{ whiteSpace: 'nowrap' }}>
            {!newItem && <>
                <Button size="sm" color="primary" className="me-1" disabled={!different() || saving} onClick={async () => {
                    if (!newSite?.trim()) { setSiteError(true); return; }
                    if (!selection?.trim()) { setStateError(true); return; }
                    const normalizedOffset = parseUtcOffset(newManualUtcOffset);
                    if (normalizedOffset === null) { setManualOffsetError(true); return; }
                    if (normalizedOffset && !newTimeZoneId?.trim()) { setManualOffsetError(true); return; }
                    setSaving(true);
                    const ok = await updateLookup({ id: lookup.id, site: newSite, state: selection, altState: altSelection ?? undefined, iataCode: newIataCode, timeZoneId: newTimeZoneId, manualUtcOffset: normalizedOffset });
                    if (ok) {
                        setSavedSnapshot({
                            site: newSite,
                            state: selection,
                            altState: altSelection,
                            iataCode: newIataCode,
                            timeZoneId: newTimeZoneId,
                            manualUtcOffset: normalizedOffset ?? ''
                        });
                    }
                    setSaving(false);
                }}>{saving ? 'Saving...' : 'Save'}</Button>
                <Button size="sm" disabled={!different() || saving} onClick={() => {
                    setNewSite(site);
                    setSelection(state);
                    setAltSelection(altState ?? '');
                    setNewIataCode(iataCode ?? "");
                    setNewTimeZoneId(timeZoneId ?? "");
                    setNewManualUtcOffset(manualUtcOffset ?? "");
                    setStateError(false);
                    setManualOffsetError(false);
                }}>Cancel</Button>
            </>}
            {newItem && <>
                <Button size="sm" color="primary" className="me-1" disabled={saving} onClick={async () => {
                    if (!newSite?.trim()) { setSiteError(true); return; }
                    if (!selection?.trim()) { setStateError(true); return; }
                    const normalizedOffset = parseUtcOffset(newManualUtcOffset);
                    if (normalizedOffset === null) { setManualOffsetError(true); return; }
                    if (normalizedOffset && !newTimeZoneId?.trim()) { setManualOffsetError(true); return; }
                    setSaving(true);
                    await saveNewLookup({ id: lookup.id, site: newSite, state: selection, altState: altSelection ?? undefined, iataCode: newIataCode, timeZoneId: newTimeZoneId, manualUtcOffset: normalizedOffset });
                    setSaving(false);
                }}>{saving ? 'Saving...' : 'Save'}</Button>
                <Button size="sm" disabled={saving} onClick={() => { cancelNew?.(); }}>Cancel</Button>
            </>}
        </td>
        <td style={{ whiteSpace: 'nowrap' }}>
            {!newItem && !deleteItem && <Button size="sm" color="danger" outline onClick={() => setDeleteItem(true)}>Delete</Button>}
            {!newItem && deleteItem && <>
                <Button size="sm" color="danger" className="me-1" onClick={async () => {
                    await deleteLookup(lookup);
                }}>Confirm</Button>
                <Button size="sm" onClick={() => setDeleteItem(false)}>Cancel</Button>
            </>}
        </td>
    </tr>
}

interface State {
    code: string;
    country: string;
    id: string;
}

const getStateCodes = (states?: State[]) => states instanceof Array ? states.map(state => state.code).sort((a, b) => a.localeCompare(b)) : [];

const AirportLookupEditor = () => {

    const [states, loading, error] = useFetch<State[]>(authEndpoints.getLocationStates);
    const [airportLookups, loadingAirportLookups, airportLookupsError, refresh] = useFetchCached<IAirportLookup[]>(endpoints.airportLookupAll, { forceRefresh: true });
    const [items, setItems] = useState(airportLookups instanceof Array ? airportLookups : []);
    const [options, setOptions] = useState(getStateCodes(states));
    const [newItemIds, setNewItemIds] = useState<Set<string>>(new Set());
    const [status, setStatus] = useState<StatusMessage | null>(null);
    const fetch = useFetchSimple();

    const showStatus = (s: StatusMessage) => {
        setStatus(s);
        // auto-clear after 4 seconds
        setTimeout(() => setStatus(null), 4000);
    };
    useEffect(() => {
        if (airportLookups instanceof Array) setItems(airportLookups);
    }, [airportLookups]);

    useEffect(() => {
        setOptions(getStateCodes(states));
    }, [states]);

    const initialLoad = (loadingAirportLookups && items.length === 0) || (loading && options.length === 0);

    const postSubmit = async (url: string, values: Object, verb: string): Promise<boolean> => {
        const options = { method: 'POST', body: JSON.stringify(values) };
        try {
            const result = await fetch(url, options).catch((e) => {
                console.error(`Airport Lookup – network error during ${verb}:`, e);
                showStatus({ message: 'A network error occurred. Please check your connection and try again.', type: 'danger' });
                throw e;
            });

            const ret = result.body;
            if (!ret.success) {
                console.error(`Airport Lookup – server rejected ${verb}:`, ret);
                const detail = ret.messages && ret.messages.length ? ret.messages[0] : 'Please review the details and try again.';
                showStatus({ message: `Could not ${verb.toLowerCase()}: ${detail}`, type: 'danger' });
                return false;
            }
            return true;
        }
        catch (error) {
            console.error(`Airport Lookup – unexpected error during ${verb}:`, error);
            showStatus({ message: 'An unexpected error occurred. Please try again or contact support.', type: 'danger' });
            return false;
        }
    };

    const updateLookup = async (e: IAirportLookup): Promise<boolean> => {
        const ok = await postSubmit(endpoints.updateAirportLookup, e, 'Updated');
        if (ok) {
            setItems(prev => prev.map(x => x.id === e.id ? { ...x, ...e, manualUtcOffset: e.manualUtcOffset ?? '' } : x));
            showStatus({ message: `"${e.site}" saved.`, type: 'success' });
            refresh();
        }
        return ok;
    }
    const saveNewLookup = async (e: IAirportLookup): Promise<boolean> => {
        const ok = await postSubmit(endpoints.addAirportLookup, e, 'Saved');
        if (ok) {
            setNewItemIds(prev => { const s = new Set(prev); s.delete(e.id); return s; });
            showStatus({ message: `"${e.site}" added.`, type: 'success' });
            refresh();
        }
        return ok;
    }
    const deleteLookup = async (e: IAirportLookup) => {
        // Optimistic: remove immediately and restore on failure
        setItems(prev => prev.filter(x => x.id !== e.id));
        try {
            const ok = await postSubmit(endpoints.deleteAirportLookup, e, 'Delete');
            if (ok) {
                showStatus({ message: `"${e.site}" deleted.`, type: 'success' });
                return;
            }

            // postSubmit returns false for server-side validation/rejection.
            setItems(prev => [e, ...prev]);
        } catch {
            setItems(prev => [e, ...prev]);
        }
    }

    const newItem = () => {
        const newId = crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        setNewItemIds(prev => new Set(prev).add(newId));
        setItems(prev => [{ id: newId, site: '', state: '', timeZoneId: '', manualUtcOffset: '' }, ...prev]);
    }

    const cancelNew = (id: string) => {
        setNewItemIds(prev => { const s = new Set(prev); s.delete(id); return s; });
        setItems(prev => prev.filter(x => x.id !== id));
    }

    return <React.Fragment>
        {initialLoad ? <LoadingAnimation height={80} /> : <>
            {error ? <h6>Error loading states</h6> : null}
            {airportLookupsError ? <h3>Error loading airport data</h3> : null}
            <div>
                <div className="mb-2 d-flex align-items-center justify-content-between">
                    <Button color="success" onClick={() => { newItem() }}>+ Add Airport</Button>
                    <Button color="success" outline title="Download as Excel" onClick={() => exportToExcel(
                        items,
                        [
                            { column: 'Site',            type: String, value: (r: IAirportLookup) => r.site ?? '' },
                            { column: 'State',           type: String, value: (r: IAirportLookup) => r.state ?? '' },
                            { column: 'Alt State',       type: String, value: (r: IAirportLookup) => r.altState ?? '' },
                            { column: 'IATA',            type: String, value: (r: IAirportLookup) => r.iataCode ?? '' },
                            { column: 'Timezone (IANA)', type: String, value: (r: IAirportLookup) => r.timeZoneId ?? '' },
                            { column: 'Manual UTC Offset', type: String, value: (r: IAirportLookup) => r.manualUtcOffset ?? '' },
                        ],
                        'AirportLookup.xlsx'
                    )}><FontAwesomeIcon icon={faFileExcel} /> Export</Button>
                </div>
                {status && (
                    <div className={`alert alert-${status.type} text-center fw-semibold py-2 mb-2`} role="alert">
                        {status.message}
                    </div>
                )}
                <div style={{ maxHeight: '70vh', overflowY: 'auto', overflowX: 'auto' }}>
                    <table className="table table-sm table-bordered align-middle mb-0">
                        <thead className="table-dark" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                            <tr>
                                <th style={{ minWidth: 180 }} title="Name of the airport or landing site">Site</th>
                                <th style={{ minWidth: 130 }} title="State or territory - used to determine the timezone when no exact match is found">State</th>
                                <th style={{ minWidth: 130 }} title="Alternative state for border sites that operate on a different timezone (e.g. Broken Hill is NSW but uses SA time)">Alt State</th>
                                <th style={{ width: 60 }} title="3-letter airport code (e.g. BNE). Used to match routes that reference an airport by code instead of full name">IATA</th>
                                <th title="Exact timezone for this site (e.g. Australia/Adelaide). Overrides the state default - required for correct local times on itineraries">Timezone (IANA)</th>
                                <th title="Optional fixed UTC offset override (format: +HH:mm or -HH:mm). Requires Timezone (IANA) to be set for this airport.">Manual UTC Offset</th>
                                <th>Save / Cancel</th>
                                <th>Delete</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(x => <LookupRow options={options} key={x.id} newItem={newItemIds.has(x.id)} updateLookup={updateLookup} saveNewLookup={saveNewLookup} deleteLookup={deleteLookup} cancelNew={() => cancelNew(x.id)} onStatus={showStatus} lookup={x} />)}
                        </tbody>
                    </table>
                </div>
            </div>
        </>}
    </React.Fragment>

}

export default AirportLookupEditor;